import React, { useEffect } from "react";
import { useLocation, Route, Routes, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Row,
  Col,
  Container,
} from "reactstrap";
import routes from "routes.js";
import axios from "axios";
import { api } from "./config/middleware";
import { API_GETPACKAGE, API_REGISTER, API_USERNANE_DUP } from "./config/api";
import bgLogin from "./assets/background-login.jpg";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mainContent = React.useRef(null);

  useEffect(() => {
    document.body.classList.add("no-sidebar");
    return () => document.body.classList.remove("no-sidebar");
  }, []);

  useEffect(() => {
    document.body.classList.add("bg-default");
    return () => {
      document.body.classList.remove("bg-default");
    };
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) mainContent.current.scrollTop = 0;
  }, [location]);

  const getRoutes = (routes) =>
    routes.map((prop, key) =>
      prop.layout === "/auth" ? (
        <Route path={prop.path} element={prop.component} key={key} exact />
      ) : null,
    );

  // ---------- form fields ----------
  const [fullName, setFullName] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [mobile, setMobile] = React.useState("");
  const [shopName, setShopName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [refCode, setRefCode] = React.useState("");

  // ---------- submit state ----------
  const [submitting, setSubmitting] = React.useState(false);

  // ---------- packages ----------
  const [pkgList, setPkgList] = React.useState([]);
  const [pkgLoading, setPkgLoading] = React.useState(false);
  const [pkgError, setPkgError] = React.useState("");
  const [selectedPkg, setSelectedPkg] = React.useState("");

  // ---------- duration ----------
  const [durationMode, setDurationMode] = React.useState("auto");
  const [duration, setDuration] = React.useState("");
  const durationOptions = [
    { value: "3 เดือน", label: "3 เดือน" },
    { value: "6 เดือน", label: "6 เดือน" },
    { value: "1 ปี", label: "1 ปี" },
  ];
  const durationToDays = (txt) => {
    const t = String(txt || "").trim();
    if (t === "1 เดือน" || t === "30 วัน") return 30;
    if (t === "3 เดือน") return 90;
    if (t === "6 เดือน") return 180;
    if (t === "1 ปี" || t === "12 เดือน") return 365;
    return 0;
  };

  // ---------- username duplication check ----------
  const [usernameChecking, setUsernameChecking] = React.useState(false);
  const [usernameError, setUsernameError] = React.useState(""); // ข้อความใต้ช่อง (เช่น "ชื่อนี้ถูกใช้แล้ว")
  const dupCtrlRef = React.useRef(null); // abort previous request

  const checkUsernameDuplicate = async () => {
    const u = username.trim();
    setUsernameError("");

    // ไม่เช็คถ้าว่างหรือสั้นเกินไป
    if (!u) return;
    if (u.length < 3) {
      setUsernameError("Username ควรมีอย่างน้อย 3 ตัวอักษร");
      return;
    }

    // ยกเลิกคำขอเดิมถ้ายังไม่เสร็จ
    if (dupCtrlRef.current) dupCtrlRef.current.abort();
    const controller = new AbortController();
    dupCtrlRef.current = controller;

    try {
      setUsernameChecking(true);

      const res = await api.post(
        API_USERNANE_DUP,
        { USERNAME: u },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
          signal: controller.signal,
        },
      );

      const resultText = String(res?.data.statustext);
      console.log("resultText", resultText);
      console.log("res", res);

      if (resultText === "FAILED") {
        setUsernameError("ชื่อนี้ถูกใช้แล้ว");
      } else if (resultText === "SUCCESS") {
        setUsernameError("");
      }
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("check username dup failed:", err);
      setUsernameError("ตรวจสอบชื่อผู้ใช้ไม่สำเร็จ ลองใหม่อีกครั้ง");
    } finally {
      setUsernameChecking(false);
      dupCtrlRef.current = null;
    }
  };

  // ---------- load packages ----------
  useEffect(() => {
    const controller = new AbortController();
    const fetchPackages = async () => {
      setPkgLoading(true);
      setPkgError("");
      try {
        const res = await api.get(API_GETPACKAGE, {
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
        });
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setPkgList(list);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error("Load packages failed:", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "โหลดรายชื่อแพ็คเกจไม่สำเร็จ";
        setPkgError(msg);
      } finally {
        setPkgLoading(false);
      }
    };
    fetchPackages();
    return () => controller.abort();
  }, []);

  // ---------- duration mode change with package ----------
  useEffect(() => {
    const pkg = pkgList.find((p) => String(p.ppId) === String(selectedPkg));
    if (pkg && String(pkg.ppName).toLowerCase() === "free") {
      setDurationMode("auto");
      setDuration("1 เดือน");
    } else if (selectedPkg) {
      setDurationMode("select");
      setDuration(durationOptions[0].value);
    } else {
      setDurationMode("auto");
      setDuration("");
    }
  }, [selectedPkg, pkgList]);

  const handleRegister = async () => {
    // ตรวจสอบเบื้องต้น
    if (
      !fullName ||
      !username ||
      !password ||
      !mobile ||
      !shopName ||
      !selectedPkg
    ) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }
    if (password !== confirmPassword) {
      alert("รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน");
      return;
    }
    if (usernameChecking) {
      alert("ระบบกำลังตรวจสอบชื่อผู้ใช้ กรุณารอสักครู่");
      return;
    }
    if (usernameError) {
      alert(usernameError);
      return;
    }

    const PACKAGEDAYS = durationToDays(duration);
    if (!PACKAGEDAYS) {
      alert("กรุณาเลือกระยะเวลาแพ็คเกจ");
      return;
    }

    const payload = {
      NAME: fullName,
      MOBILE: mobile,
      EMAIL: email,
      REFCODE: refCode,
      PACKAGEID: selectedPkg,
      PACKAGEDAYS,
      ORGTYPE: "",
      ORGNAME: shopName,
      USERNAME: username,
      PASSWORD: password,
    };
    console.log(`payload`, payload);

    setSubmitting(true);
    try {
      const res = await api.post(API_REGISTER, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });

      const resultText = res?.data?.statustext;
      console.log(`resultText`, resultText);

      if (String(resultText).trim().toUpperCase() === "SUCCESS") {
        alert("สมัครใช้งานสำเร็จ");
        navigate("/login");
      } else {
        throw new Error(
          res?.data?.message ||
            `สมัครใช้งานไม่สำเร็จ (${resultText || "ERROR"})`,
        );
      }
    } catch (err) {
      console.error("Register failed:", err);
      const msg =
        err?.response?.data?.message || err?.message || "สมัครใช้งานไม่สำเร็จ";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="main-content login-bg"
        ref={mainContent}
        style={{
          minHeight: "100vh",
          backgroundImage: `url(${bgLogin})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.50)",
            zIndex: 0,
          }}
        />
        {/* Header */}
        <div className="header py-1 py-lg-1">
          <Container>
            <div className="header-body text-center mb-7"></div>
          </Container>
        </div>
        {/* Login Card */}
        <Container
          fluid
          className="pb-5"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Row className="justify-content-center w-100">
            <Col lg="5" md="7">
              <Card className="bg-secondary shadow border-0">
                <CardBody className="px-lg-5 py-lg-5">
                  <div className="text-center text-muted mb-4">
                    <h3 className="text-center mb-4">สมัครใช้งาน</h3>
                  </div>
                  <Form role="form">
                    <FormGroup>
                      <label className="form-control-label mb-1 required">
                        ชื่อ-นามสกุล
                      </label>
                      <InputGroup className="input-group-alternative mb-3">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-single-02" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="ชื่อ-นามสกุล"
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>
                    <div
                      className="mb-4"
                      style={{
                        border: "1px dashed #c8ced4",
                        borderRadius: 10,
                        padding: "1rem 1.25rem 0.75rem",
                      }}
                    >
                      <FormGroup className="mb-1">
                        <label className="form-control-label mb-1 required">
                          Username
                        </label>
                        <InputGroup className="input-group-alternative">
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="ni ni-email-83" />
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            placeholder="Username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onMouseLeave={checkUsernameDuplicate} // ⬅️ เช็คเมื่อ mouse ออก
                            onBlur={checkUsernameDuplicate} // ⬅️ เผื่อคีย์บอร์ด/มือถือ
                            autoComplete="off"
                          />
                        </InputGroup>
                        {/* แสดงสถานะการเช็ค */}
                        <small className="d-block mt-1">
                          {usernameChecking ? (
                            <span className="text-muted">
                              กำลังตรวจสอบชื่อผู้ใช้…
                            </span>
                          ) : usernameError ? (
                            <span className="text-danger">{usernameError}</span>
                          ) : username.trim() ? (
                            <span className="text-success">
                              ชื่อนี้สามารถใช้ได้
                            </span>
                          ) : null}
                        </small>
                      </FormGroup>

                      <Row className="mt-3">
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <label className="form-control-label mb-1 required">
                              Password
                            </label>
                            <InputGroup className="input-group-alternative">
                              <InputGroupAddon addonType="prepend">
                                <InputGroupText>
                                  <i className="ni ni-lock-circle-open" />
                                </InputGroupText>
                              </InputGroupAddon>
                              <Input
                                placeholder="Password"
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <label className="form-control-label mb-1 required">
                              ยืนยัน Password
                            </label>
                            <InputGroup className="input-group-alternative">
                              <InputGroupAddon addonType="prepend">
                                <InputGroupText>
                                  <i className="ni ni-lock-circle-open" />
                                </InputGroupText>
                              </InputGroupAddon>
                              <Input
                                placeholder="ยืนยัน Password"
                                type="password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) =>
                                  setConfirmPassword(e.target.value)
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>

                      <small className="text-danger d-block">
                        *** กรุณาจดจำ Username และ Password
                        ของท่านเพื่อใช้ในการเข้าสู่ระบบ
                      </small>
                    </div>

                    <FormGroup>
                      <label className="form-control-label mb-1 required">
                        เบอร์โทร
                      </label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-mobile-button" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="เบอร์โทร"
                          type="text"
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>

                    <FormGroup>
                      <label className="form-control-label mb-1 required">
                        ชื่อร้าน
                      </label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-building" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="ชื่อร้าน"
                          type="text"
                          value={shopName}
                          onChange={(e) => setShopName(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>

                    <Row>
                      <Col md="6">
                        <label className="form-control-label mb-1 required">
                          แพ็คเกจ
                        </label>
                        <FormGroup>
                          <InputGroup className="input-group-alternative">
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>
                                <i className="ni ni-box-2" />
                              </InputGroupText>
                            </InputGroupAddon>
                            <Input
                              type="select"
                              value={selectedPkg}
                              onChange={(e) => setSelectedPkg(e.target.value)}
                              disabled={pkgLoading}
                              autoComplete="off"
                            >
                              <option value="">
                                {pkgLoading ? "กำลังโหลด..." : "เลือกแพ็คเกจ"}
                              </option>
                              {pkgList.map((p) => (
                                <option key={p.ppId} value={p.ppId}>
                                  {p.ppName}
                                </option>
                              ))}
                            </Input>
                          </InputGroup>
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <label className="form-control-label mb-1">
                          ระยะเวลา
                        </label>
                        <FormGroup>
                          <InputGroup className="input-group-alternative">
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>
                                <i className="fas fa-clock" />
                              </InputGroupText>
                            </InputGroupAddon>

                            {durationMode === "auto" ? (
                              <Input
                                className="pl-2"
                                type="text"
                                value={duration || ""}
                                placeholder="ระยะเวลา"
                                readOnly
                              />
                            ) : (
                              <Input
                                className="pl-2"
                                type="select"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                              >
                                {durationOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Input>
                            )}
                          </InputGroup>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="my-4">
                      <Col xs="12">
                        <div className="custom-control custom-control-alternative custom-checkbox">
                          <input
                            className="custom-control-input"
                            id="customCheckRegister"
                            type="checkbox"
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="customCheckRegister"
                          >
                            <span className="text-muted">
                              ยอมรับ{" "}
                              <a href="#!" onClick={(e) => e.preventDefault()}>
                                ข้อตกลงการใช้บริการ
                              </a>
                            </span>
                          </label>
                        </div>
                      </Col>
                    </Row>

                    <div className="text-center">
                      <Button
                        className="my-4"
                        color="primary"
                        type="button"
                        onClick={handleRegister}
                        disabled={
                          submitting || usernameChecking || !!usernameError
                        }
                      >
                        {submitting ? "กำลังสมัคร..." : "สมัครใช้งาน"}
                      </Button>
                    </div>

                    <Row className="justify-content-center">
                      <div className="d-flex align-items-center text-center">
                        <span className="mr-1 small">
                          คุณมีบัญชีใช้งานแล้ว?
                        </span>
                        <Button
                          color="link"
                          className="p-0 align-baseline"
                          type="button"
                          onClick={() => navigate("/login")}
                        >
                          เข้าสู่ระบบ
                        </Button>
                      </div>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Register;
